import React, { useCallback, useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Offcanvas from 'react-bootstrap/Offcanvas';
import { Link, Outlet } from 'react-router-dom';
import Sidebar from './Sidebar';

/** Bootstrap-Breakpoint "lg" (ab hier wird die feste Desktop-Sidebar gezeigt). */
const DESKTOP_MEDIA_QUERY = '(min-width: 992px)';

/**
 * Grundlayout der Anwendung: Sidebar-Navigation + Inhaltsbereich (Outlet).
 *
 * Responsive (mobile-first):
 * - < lg: Topbar mit Menue-Button, Sidebar als einklappbares Offcanvas
 * - >= lg: feste Sidebar-Spalte links
 *
 * Hinweis: Bewusst KEIN `responsive="lg"` am Offcanvas — React-Bootstrap wuerde
 * den Offcanvas-Inhalt ab lg permanent inline rendern und damit die separate
 * Desktop-Sidebar duplizieren. Stattdessen schliesst ein matchMedia-Listener
 * das Offcanvas, sobald der Viewport auf >= lg waechst.
 */
function AppLayout(): React.JSX.Element {
  const [showOffcanvas, setShowOffcanvas] = useState<boolean>(false);

  const openOffcanvas = useCallback((): void => setShowOffcanvas(true), []);
  const closeOffcanvas = useCallback((): void => setShowOffcanvas(false), []);

  // Offcanvas darf bei Resize auf >= lg nicht offen ueber der Desktop-Sidebar haengen bleiben
  useEffect(() => {
    const mediaQueryList = window.matchMedia(DESKTOP_MEDIA_QUERY);
    const handleChange = (event: MediaQueryListEvent): void => {
      if (event.matches) {
        setShowOffcanvas(false);
      }
    };
    mediaQueryList.addEventListener('change', handleChange);
    return () => mediaQueryList.removeEventListener('change', handleChange);
  }, []);

  return (
    <div id="app-layout" className="app-layout">
      {/* Desktop-Sidebar */}
      <aside id="app-sidebar-desktop" className="app-sidebar d-none d-lg-flex flex-column">
        <div id="sidebar-desktop-header" className="px-3 py-3 border-bottom border-secondary">
          <Link to="/" id="sidebar-desktop-brand" className="sidebar-brand">
            <i className="bi bi-buildings me-2" id="sidebar-desktop-brand-icon"></i>
            MVGen Management
          </Link>
        </div>
        <div id="sidebar-desktop-body" className="py-3 flex-grow-1 d-flex flex-column">
          <Sidebar idPrefix="sidebar-desktop" />
        </div>
      </aside>

      <div id="app-main" className="flex-grow-1 d-flex flex-column min-vh-100">
        {/* Mobile-Topbar */}
        <header
          id="app-topbar"
          className="app-topbar d-lg-none d-flex align-items-center gap-2 px-3 py-2"
        >
          <Button
            id="topbar-menu-button"
            variant="outline-light"
            size="sm"
            onClick={openOffcanvas}
          >
            <i className="bi bi-list" id="topbar-menu-icon"></i>
          </Button>
          <Link to="/" id="topbar-brand" className="sidebar-brand">
            MVGen Management
          </Link>
        </header>

        {/* Inhaltsbereich */}
        <main id="app-content" className="app-content">
          <Outlet />
        </main>
      </div>

      {/* Mobile-Offcanvas-Sidebar */}
      <Offcanvas
        id="app-offcanvas"
        className="app-offcanvas"
        backdropClassName="app-offcanvas-backdrop"
        show={showOffcanvas}
        onHide={closeOffcanvas}
        placement="start"
      >
        <Offcanvas.Header id="app-offcanvas-header">
          <Offcanvas.Title id="app-offcanvas-title">MVGen Management</Offcanvas.Title>
          <Button
            id="offcanvas-close-button"
            variant="outline-light"
            size="sm"
            onClick={closeOffcanvas}
          >
            <i className="bi bi-x-lg" id="offcanvas-close-icon"></i>
          </Button>
        </Offcanvas.Header>
        <Offcanvas.Body id="app-offcanvas-body" className="px-0 py-3 d-flex flex-column">
          <Sidebar idPrefix="sidebar-mobile" onNavigate={closeOffcanvas} />
        </Offcanvas.Body>
      </Offcanvas>
    </div>
  );
}

export default AppLayout;
